/**
 * Wizard step 1 — property identity. Street address, city/state/zip (three
 * columns), specs, strategy, owned status, purchase date, and an optional photo
 * URL. Selecting a strategy re-seeds the financing defaults via `onStrategyChange`.
 */
import { useEffect, useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { ArrowUpFromLine, ImageIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/ui/date-input";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

import type { AddPropertyFormValues } from "@/components/properties/add-property/schema";
import type { Strategy } from "@/types/pipeline";

interface StepPropertyProps {
  form: UseFormReturn<AddPropertyFormValues>;
  imageFile: File | null;
  onImageFileChange: (file: File | null) => void;
  onStrategyChange: (strategy: Strategy) => void;
}

export function StepProperty({
  form,
  imageFile,
  onImageFileChange,
  onStrategyChange,
}: StepPropertyProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  // Object URL preview for the selected file; revoked when it changes/unmounts.
  useEffect(() => {
    if (!imageFile) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image exceeds the 10MB limit");
      return;
    }
    onImageFileChange(file);
    // A chosen file supersedes any pasted URL.
    form.setValue("imgUrl", "");
  };

  const urlValue = form.watch("imgUrl");
  const previewSrc = preview ?? (urlValue ? urlValue : null);

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
      <FormField
        control={form.control}
        name="addr1"
        render={({ field }) => (
          <FormItem className="sm:col-span-6">
            <FormLabel>Street address</FormLabel>
            <FormControl>
              <Input placeholder="412 Mountain View Drive" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="city"
        render={({ field }) => (
          <FormItem className="sm:col-span-3">
            <FormLabel>City</FormLabel>
            <FormControl>
              <Input placeholder="Birmingham" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="state"
        render={({ field }) => (
          <FormItem className="sm:col-span-1">
            <FormLabel>State</FormLabel>
            <FormControl>
              <Input placeholder="AL" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="zip"
        render={({ field }) => (
          <FormItem className="sm:col-span-2">
            <FormLabel>ZIP</FormLabel>
            <FormControl>
              <Input placeholder="35214" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="bd"
        render={({ field }) => (
          <FormItem className="sm:col-span-2">
            <FormLabel>Beds</FormLabel>
            <FormControl>
              <NumberInput placeholder="3" value={field.value} onChange={field.onChange} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="ba"
        render={({ field }) => (
          <FormItem className="sm:col-span-2">
            <FormLabel>Baths</FormLabel>
            <FormControl>
              <NumberInput
                placeholder="2"
                step="0.5"
                value={field.value}
                onChange={field.onChange}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="sqft"
        render={({ field }) => (
          <FormItem className="sm:col-span-2">
            <FormLabel>Square feet</FormLabel>
            <FormControl>
              <NumberInput placeholder="1,890" value={field.value} onChange={field.onChange} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="strategy"
        render={({ field }) => (
          <FormItem className="sm:col-span-2">
            <FormLabel>Strategy</FormLabel>
            <Select
              value={field.value}
              onValueChange={(v) => {
                field.onChange(v);
                onStrategyChange(v as Strategy);
              }}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Rental">Rental</SelectItem>
                <SelectItem value="BRRRR">BRRRR</SelectItem>
                <SelectItem value="Flip">Flip</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="status"
        render={({ field }) => (
          <FormItem className="sm:col-span-2">
            <FormLabel>Status</FormLabel>
            <Select value={field.value} onValueChange={field.onChange}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="owned">Owned</SelectItem>
                <SelectItem value="in-rehab">In Rehab</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="purchaseDate"
        render={({ field }) => (
          <FormItem className="sm:col-span-2">
            <FormLabel>Purchase date</FormLabel>
            <FormControl>
              <DateInput {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormItem className="sm:col-span-6">
        <FormLabel>Property photo (optional)</FormLabel>
        <div className="flex items-center gap-3">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted">
            {previewSrc ? (
              <img src={previewSrc} alt="Property preview" className="h-full w-full object-cover" />
            ) : (
              <ImageIcon className="h-6 w-6 text-muted-foreground" aria-hidden />
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              handleFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
            <ArrowUpFromLine className="mr-1.5 h-4 w-4" /> Upload image
          </Button>
          {imageFile && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => onImageFileChange(null)}
              className="text-muted-foreground"
            >
              Remove
            </Button>
          )}
        </div>
      </FormItem>

      <FormField
        control={form.control}
        name="imgUrl"
        render={({ field }) => (
          <FormItem className="sm:col-span-6">
            <FormLabel>Or paste an image URL</FormLabel>
            <FormControl>
              <Input
                type="url"
                placeholder="https://…"
                {...field}
                disabled={imageFile != null}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
